import { CBox_GetZoneListResponseItem } from "@server/services/cbox/public/api/v1/resources/zone/types";
import { TreeNode } from "primeng/api";

// Depth-first position of every zone in the picker tree, used to keep the
// requested zones (and so the report groups) in picker order instead of
// click order.
export function zoneTreeOrder(roots: TreeNode[]): Map<number, number> {
  const order = new Map<number, number>();
  const walk = (nodes: TreeNode[]): void => {
    for (const node of nodes) {
      order.set(node.data as number, order.size);
      if (node.children) {
        walk(node.children);
      }
    }
  };

  walk(roots);

  return order;
}

export function buildZoneTree(
  zones: CBox_GetZoneListResponseItem[]): TreeNode[] {
  const nodesById = new Map<number, TreeNode>();

  for (const zone of zones) {
    nodesById.set(zone.id, {
      key: String(zone.id),
      label: zone.name,
      data: zone.id,
      children: [],
    });
  }
  const roots: TreeNode[] = [];

  for (const zone of zones) {
    const node = nodesById.get(zone.id);
    const parentId = zone.parent?.id;
    const parent = parentId ? nodesById.get(parentId) : undefined;

    if (node && parent) {
      parent.children?.push(node);
    } else if (node) {
      roots.push(node);
    }
  }
  // Leaf nodes must not keep an empty children array (renders a toggle).
  for (const node of nodesById.values()) {
    if (node.children && node.children.length === 0) {
      delete node.children;
    }
  }

  return roots;
}

// Fresh node copies, so two tree-selects never share mutable node state
// (PrimeNG writes selection/expansion state onto the nodes it renders).
export function cloneZoneTree(nodes: TreeNode[]): TreeNode[] {
  return nodes.map(node => ({
    key: node.key,
    label: node.label,
    data: node.data,
    ...(node.children ? { children: cloneZoneTree(node.children) } : {}),
  }));
}

export function zoneSelectionLabel(selected: TreeNode[] | undefined,
  loading: boolean): string {
  if (!selected?.length) {
    return loading ? "Se încarcă zonele…" : "Selectează zone";
  }
  if (selected.length <= 2) {
    return selected.map(node => node.label).join(", ");
  }

  // Romanian puts "de" before the noun from 20 on ("20 de zone").
  const of = new Intl.PluralRules("ro-RO").select(selected.length) === "other"
    ? "de "
    : "";

  return `${selected.length} ${of}zone selectate`;
}
